import {Component, ElementRef, Inject, LOCALE_ID, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Role} from '../../model/auth/Role';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ProgressService} from '../../service/progress.service';
import {BaseModalComponent} from '../../common/modal/base-modal/base-modal.component';
import {NotificationHandler} from '../../helper/notification.handler';
import {Observable} from 'rxjs';
import {EditorComponent} from '@tinymce/tinymce-angular';
import {EditorConfigHelper} from '../../helper/editor-config.helper';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {Contest, ContestConfig, ContestDto} from '../../model/contest/Contest';
import {ContestService} from '../../service/contest.service';
import {
  CustomValidators,
  EnrolTimeMismatchErrorStateMatcher,
  StartTimeMismatchErrorStateMatcher
} from '../../helper/custom.validators';
import {NumberHelper} from '../../helper/NumberHelper';
import {MAT_DATE_RANGE_SELECTION_STRATEGY} from '@angular/material/datepicker';
import {FutureDayRangeSelectionStrategy} from '../../helper/date-range.strategy';
import {EventCategory, EventType, TrackingService} from '../../service/tracking.service';
import {AssetType} from '../../model/asset/Asset';
import {AssetService} from '../../service/asset.service';

export interface ContestDetailsData {
  competitionId: string;
  contest?: Contest;
  readOnly: boolean;
}

@Component({
  selector: 'app-contest-details-modal',
  templateUrl: './contest-details-modal.component.html',
  styleUrls: ['./contest-details-modal.component.scss'],
  providers: [{
    provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
    useClass: FutureDayRangeSelectionStrategy
  }]
})
export class ContestDetailsModalComponent extends BaseModalComponent {

  readonly roles = [Role.Host];

  @ViewChild('dialogContent', {static: true}) protected progressContainer!: ElementRef;
  @ViewChild('editorComponent', {static: true}) editor!: EditorComponent;

  nameField = new FormControl('', [
    Validators.required, Validators.minLength(3), Validators.maxLength(128)
  ]);
  descriptionField = new FormControl('', [
    Validators.maxLength(3072)
  ]);
  instructionsField = new FormControl('', [
    Validators.maxLength(3072)
  ]);
  priceField = new FormControl('', [
    Validators.required, Validators.min(0)
  ]);
  paymentRequiredField = new FormControl('', [
    Validators.required,
  ]);
  durationField = new FormControl('', [
    Validators.required, Validators.min(1), Validators.max(2880)
  ]);
  pointsBalanceField = new FormControl('', [
    Validators.required, Validators.min(0), Validators.max(200)
  ]);
  answerPenaltyField = new FormControl('', [
    Validators.required, Validators.min(0), Validators.max(100)
  ]);
  excellentRatioField = new FormControl('', [
    Validators.required, Validators.min(1), Validators.max(100)
  ]);
  goodRatioField = new FormControl('', [
    Validators.required, Validators.min(1), Validators.max(100)
  ]);
  averageRatioField = new FormControl('', [
    Validators.required, Validators.min(1), Validators.max(100)
  ]);

  startsAtDateField = new FormControl('', [Validators.required]);
  startsAtTimeField = new FormControl('', [Validators.required]);
  endsAtDateField = new FormControl('', [Validators.required]);
  endsAtTimeField = new FormControl('', [Validators.required]);
  enrolStartsAtDateField = new FormControl('', [Validators.required]);
  enrolStartsAtTimeField = new FormControl('', [Validators.required]);
  enrolEndsAtDateField = new FormControl('', [Validators.required]);
  enrolEndsAtTimeField = new FormControl('', [Validators.required]);
  detailsForm: FormGroup;

  enrolMatcher = new EnrolTimeMismatchErrorStateMatcher();
  startMatcher = new StartTimeMismatchErrorStateMatcher();

  readonly today = new Date();

  readonly competitionId?: string;
  readonly contestId?: string;
  contest?: Contest;
  action: string;

  initEditor: boolean = false;
  editorConfig: object;
  instructionsEditorConfig: object;

  taskBookFile?: File;

  readOnlyMode: boolean;

  constructor(private contestService: ContestService,
              private trackingService: TrackingService,
              private assetService: AssetService,
              dialogRef: MatDialogRef<ContestDetailsModalComponent>,
              progressService: ProgressService,
              breakpointObserver: BreakpointObserver,
              formBuilder: FormBuilder,
              @Inject(LOCALE_ID) locale: string,
              @Inject(MAT_DIALOG_DATA) data: ContestDetailsData) {
    super(dialogRef, progressService);

    this.competitionId = data ? data.competitionId : undefined;

    this.readOnlyMode = data.readOnly != undefined ? data.readOnly : true;

    if (data && data.contest && data.contest.id) {
      this.action = this.readOnlyMode
        ? $localize`:General|View action label@@general-view-action-label:View`
        : $localize`:General|Edit action label@@general-edit-action-label:Edit`;
      this.contestId = data.contest.id;
    } else {
      this.action = $localize`:General|Create action label@@general-create-action-label:Create`;
    }

    this.detailsForm = formBuilder.group({
      name: this.nameField,
      description: this.descriptionField,
      instructions: this.instructionsField,
      price: this.priceField,
      paymentRequired: this.paymentRequiredField,
      duration: this.durationField,
      pointsBalance: this.pointsBalanceField,
      answerPenalty: this.answerPenaltyField,
      excellentRatio: this.excellentRatioField,
      goodRatio: this.goodRatioField,
      averageRatio: this.averageRatioField,
      startsAtDate: this.startsAtDateField,
      startsAtTime: this.startsAtTimeField,
      endsAtDate: this.endsAtDateField,
      endsAtTime: this.endsAtTimeField,
      enrolStartsAtDate: this.enrolStartsAtDateField,
      enrolStartsAtTime: this.enrolStartsAtTimeField,
      enrolEndsAtDate: this.enrolEndsAtDateField,
      enrolEndsAtTime: this.enrolEndsAtTimeField
    }, {validators: [CustomValidators.enrolTimeCorrect, CustomValidators.startTimeCorrect]});

    const isMobile = breakpointObserver.isMatched(Breakpoints.HandsetPortrait);
    const descriptionPlaceholder = $localize`:Contest Details Modal|Contest Details modal description input placeholder@@contest-details-modal-description-input-placeholder:Contest description...`;
    this.editorConfig = EditorConfigHelper.config(locale, descriptionPlaceholder, !isMobile, 220);
    const instructionsPlaceholder = $localize`:Contest Details Modal|Contest Details modal instructions input placeholder@@contest-details-modal-instructions-input-placeholder:Contest instructions (shown to participants before they start)...`;
    this.instructionsEditorConfig = EditorConfigHelper.config(locale, instructionsPlaceholder, !isMobile, 220);
  }

  initContent() {
    if (this.contestId) {
      this.loadContest(this.contestId);
    } else {
      setTimeout(() => {
        this.initEditor = true;
      }, 100);

      this.enrolStartsAtTimeField.setValue('00:00');
      this.enrolEndsAtTimeField.setValue('23:59');

      this.startsAtTimeField.setValue('09:00');
      this.endsAtTimeField.setValue('18:00');

      this.priceField.setValue(0);
      this.paymentRequiredField.setValue(true);
      this.durationField.setValue(90);
      this.pointsBalanceField.setValue(0);
      this.answerPenaltyField.setValue(0);
      this.excellentRatioField.setValue(ContestConfig.DEFAULT_EXCELLENT_RATIO * 100);
      this.goodRatioField.setValue(ContestConfig.DEFAULT_GOOD_RATIO * 100);
      this.averageRatioField.setValue(ContestConfig.DEFAULT_AVERAGE_RATIO * 100);
    }
  }

  get editorApiKey() {
    return EditorConfigHelper.editorApiKey;
  }

  addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  private static dateToTime(date: Date) {
    return `${NumberHelper.pad(date.getHours(), 2)}:${NumberHelper.pad(date.getMinutes(), 2)}`;
  }

  private loadContest(id: string) {
    this.startProgress();

    this.contestService.getDetails(id, true).subscribe({
      next: contest => {
        this.contest = contest;

        this.nameField.setValue(this.contest.name);
        this.descriptionField.setValue(this.contest.description);
        this.instructionsField.setValue(this.contest.instructions);
        this.priceField.setValue(Math.floor((this.contest.price as number) / 100) || 0);
        this.paymentRequiredField.setValue(this.contest.paymentRequired);
        if (this.contest?.price == 0) {
          this.paymentRequiredField.disable();
        }
        if (this.contest.config) {
          this.durationField.setValue(this.contest.config.duration);
          this.pointsBalanceField.setValue(this.contest.config.initialPointsBalance);
          this.answerPenaltyField.setValue((this.contest.config.answerPenalty || 0) * 100);
          this.excellentRatioField.setValue((this.contest.config.excellentRatio || 0) * 100);
          this.goodRatioField.setValue((this.contest.config.goodRatio || 0) * 100);
          this.averageRatioField.setValue((this.contest.config.averageRatio || 0) * 100);
        }

        const startsAt = this.contest.startsAt;
        if (startsAt) {
          this.startsAtDateField.setValue(startsAt);
          this.startsAtTimeField.setValue(ContestDetailsModalComponent.dateToTime(startsAt));
          if (startsAt < this.today) {
            this.startsAtTimeField.disable();
            this.startsAtDateField.disable();
          }
        } else {
          this.startsAtTimeField.setValue('09:00');
        }
        const endsAt = this.contest.endsAt;
        if (endsAt) {
          this.endsAtDateField.setValue(endsAt);
          this.endsAtTimeField.setValue(ContestDetailsModalComponent.dateToTime(endsAt));
          if (endsAt < this.today) {
            this.endsAtTimeField.disable();
            this.endsAtDateField.disable();
          }
        } else {
          this.endsAtTimeField.setValue('18:00');
        }

        const enrolStartsAt = this.contest.enrolStartsAt;
        if (enrolStartsAt) {
          this.enrolStartsAtDateField.setValue(enrolStartsAt);
          this.enrolStartsAtTimeField.setValue(ContestDetailsModalComponent.dateToTime(enrolStartsAt));
          if (enrolStartsAt < this.today) {
            this.enrolStartsAtTimeField.disable();
            this.enrolStartsAtDateField.disable();
          }
        } else {
          this.enrolStartsAtTimeField.setValue('00:00');
        }
        const enrolEndsAt = this.contest.enrolEndsAt;
        if (enrolEndsAt) {
          this.enrolEndsAtDateField.setValue(enrolEndsAt);
          this.enrolEndsAtTimeField.setValue(ContestDetailsModalComponent.dateToTime(enrolEndsAt));
          if (enrolEndsAt < this.today) {
            this.enrolEndsAtTimeField.disable();
            this.enrolEndsAtDateField.disable();
          }
        } else {
          this.enrolEndsAtTimeField.setValue('23:59');
        }

        if (this.readOnlyMode) {
          this.nameField.disable();
          this.descriptionField.disable();
          this.instructionsField.disable();
          this.priceField.disable();
          this.paymentRequiredField.disable();
          this.durationField.disable();
          this.pointsBalanceField.disable();
          this.answerPenaltyField.disable();
          this.excellentRatioField.disable();
          this.goodRatioField.disable();
          this.averageRatioField.disable();
          this.startsAtDateField.disable();
          this.startsAtTimeField.disable();
          this.endsAtDateField.disable();
          this.endsAtTimeField.disable();
          this.enrolStartsAtDateField.disable();
          this.enrolStartsAtTimeField.disable();
          this.enrolEndsAtDateField.disable();
          this.enrolEndsAtTimeField.disable();
        }

        this.initEditor = true;

        this.stopProgress();
      }, error: error => {
        NotificationHandler.handleError($localize`:Notifications|Contest details fetch has failed message@@notifications-contest-details-fetch-failed-error:Contest details fetch has failed`, error);
        this.stopProgress();
      }
    });
  }

  proceedAllowed(): boolean {
    return this.detailsForm.valid && this.competitionId != undefined;
  }

  private static parseTime(date: Date, time: string): Date {
    const values = time.split(':');

    date.setHours(Number.parseInt(values[0]), Number.parseInt(values[1]), 0);

    return date;
  }

  async proceed(): Promise<void> {
    if (!this.competitionId) return;
    const competitionId: string = this.competitionId;

    this.startProgress();

    let observable: Observable<Contest>;

    const startsAt = this.contest && this.contest.startsAt && this.contest.startsAt <= this.today
      ? this.contest.startsAt.getTime()
      : this.startsAtDateField.value
        ? ContestDetailsModalComponent
          .parseTime(this.startsAtDateField.value as Date, this.startsAtTimeField.value as string).getTime()
        : undefined;
    const endsAt = this.contest && this.contest.endsAt && this.contest.endsAt <= this.today
      ? this.contest.endsAt.getTime()
      : this.endsAtDateField.value
        ? ContestDetailsModalComponent
          .parseTime(this.endsAtDateField.value as Date, this.endsAtTimeField.value as string).getTime()
        : undefined;
    const enrolStartsAt = this.contest && this.contest.enrolStartsAt && this.contest.enrolStartsAt <= this.today
      ? this.contest.enrolStartsAt.getTime()
      : this.enrolStartsAtDateField.value
        ? ContestDetailsModalComponent
          .parseTime(this.enrolStartsAtDateField.value as Date, this.enrolStartsAtTimeField.value as string).getTime()
        : undefined;
    const enrolEndsAt = this.contest && this.contest.enrolEndsAt && this.contest.enrolEndsAt <= this.today
      ? this.contest.enrolEndsAt.getTime()
      : this.enrolEndsAtDateField.value
        ? ContestDetailsModalComponent
          .parseTime(this.enrolEndsAtDateField.value as Date, this.enrolEndsAtTimeField.value as string).getTime()
        : undefined;

    const contest: ContestDto = {
      name: this.nameField.value as string,
      description: this.descriptionField.value as string,
      instructions: this.instructionsField.value as string,
      price: (this.priceField.value as number || 0) * 100,
      payment_required: this.priceField.value == 0 ? false : this.paymentRequiredField.value as boolean,
      config: {
        duration: parseInt(<string>this.durationField.value || '0'),
        initial_points_balance: parseInt(<string>this.pointsBalanceField.value),
        answer_penalty: NumberHelper.parsePercentage(this.answerPenaltyField.value as number),
        excellent_ratio: NumberHelper.parsePercentage(this.excellentRatioField.value as number),
        good_ratio: NumberHelper.parsePercentage(this.goodRatioField.value as number),
        average_ratio: NumberHelper.parsePercentage(this.averageRatioField.value as number),
      },
      starts_at: startsAt,
      ends_at: endsAt,
      enrol_starts_at: enrolStartsAt,
      enrol_ends_at: enrolEndsAt,
      task_info_url: this.contest?.taskInfoUrl,
    };

    try {
      if (this.taskBookFile) contest.task_info_url = await this.uploadAsset(this.taskBookFile, AssetType.exam_book);
    } catch (err) {
      this.stopProgress();
      return;
    }

    if (this.contest) {
      observable = this.contestService.update(this.contest.id, contest);
    } else {
      observable = this.contestService.create(competitionId, contest);
    }

    observable.subscribe({
      next: contest => {
        this.stopProgress();
        const data: ContestDetailsData = {
          competitionId: competitionId,
          contest: contest,
          readOnly: this.readOnlyMode,
        };

        this.trackingService.sendEvent(this.contest ? EventType.contest_update : EventType.contest_create, EventCategory.contest, contest.id);

        this.closeModal(data);
      }, error: error => {
        this.stopProgress();

        if (this.contest) {
          NotificationHandler.handleError($localize`:Notifications|Contest details update has failed message@@notifications-contest-details-update-failed-error:Contest details update has failed`, error);
        } else {
          NotificationHandler.handleError($localize`:Notifications|Contest create has failed message@@notifications-contest-create-failed-error:Contest create has failed`, error);
        }
      }
    });
  }

  taskBookSelected(files: File[]) {
    this.taskBookFile = files && files.length > 0 ? files[0] : undefined;
  }

  private uploadAsset(file: File, type: AssetType): Promise<string | undefined> {
    return new Promise<string | undefined>((resolve, reject) => {
      this.assetService.registerAssetUpload(type, file).subscribe({
        next: asset => {
          if (asset.uploadUrl && file) this.assetService.uploadContent(asset.uploadUrl, file).subscribe({
            next: () => {
              resolve(asset.url);
            }, error: error => {
              NotificationHandler.handleError($localize`:Notifications|Asset upload failed message@@notifications-asset-upload-failed-error:Asset upload has failed`, error);

              reject(error);
            }
          });
        }, error: error => {
          NotificationHandler.handleError($localize`:Notifications|Asset upload registration failed message@@notifications-asset-upload-registration-failed-error:Asset upload registration has failed`, error);

          reject(error);
        }
      });
    });
  }
}
